// @shared/types/profile-manager.types.ts
// Profile Manager specific type definitions
// Uses core types from @shared/types (no duplicates)

// Note: ProfileWithRelations is now in core/profile.types.ts
import type { ProfileWithRelations } from '../core/profile.types';

// ==================== FILTERS ====================

/**
 * Filter options for profile search
 * All filters are combined with AND logic
 */
export interface ProfileFilters {
  // Basic Filters
  search?: string;                    // Text search: name, company, title, location
  listId?: number | 'all';           // Legacy: Single List
  listIds?: number[];          // Filter by list (or 'all')
  categoryIds?: number[];             // Filter by categories (OR within, AND with other filters)
  scoreRange?: string;                // Score range ID from settings
  createdBy?: number;                 // Filter by creator user
  
  // Date Range Filters
  afterField?: 'createdAt' | 'modifiedAt' | 'exportedAt';
  afterDate?: string;                 // ISO date string (YYYY-MM-DD)
  beforeField?: 'createdAt' | 'modifiedAt' | 'exportedAt';
  beforeDate?: string;                // ISO date string (YYYY-MM-DD)
  
  // Marker Filters (Boolean)
  isExported?: boolean;               // Has been exported
  isActive?: boolean;                 // Is active profile
  isBlocked?: boolean;                // Is blocked
  isRegistered?: boolean;             // Is registered
  hasCategories?: boolean;            // Has at least one category (true) or no categories (false)
  hasLists?: boolean;                 // Has at least one list
  hasEmail?: boolean;    
}

/**
 * Default filter values
 */
export const DEFAULT_PROFILE_FILTERS: ProfileFilters = {
  search: undefined,
  listId: 'all',
  categoryIds: undefined,
  scoreRange: undefined,
  createdBy: undefined,
  afterField: undefined,
  afterDate: undefined,
  beforeField: undefined,
  beforeDate: undefined,
  isExported: undefined,
  isActive: undefined,
  isBlocked: undefined,
  isRegistered: undefined,
  hasCategories: undefined,
  hasLists: undefined,
  hasEmail: undefined,
};

// ==================== PAGINATION ====================

/**
 * Pagination state
 */
export interface PaginationState {
  page: number;           // Current page (1-indexed)
  limit: number;          // Items per page
}

/**
 * Default pagination values
 */
export const DEFAULT_PAGINATION: PaginationState = {
  page: 1,
  limit: 20
};

/**
 * Available page size options
 */
export const PAGE_SIZE_OPTIONS = [20, 50, 100, 200, 250] as const;

/**
 * Pagination metadata from backend
 */
export interface PaginationMetadata {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  total: number;
  startIndex: number;
  endIndex: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// ==================== SORTING ====================

/**
 * Sorting configuration
 */
export interface SortingState {
  field: 'name' | 'score' | 'company' | 'location' | 'createdAt';
  order: 'asc' | 'desc';
}

/**
 * Default sorting values
 */
export const DEFAULT_SORTING: SortingState = {
  field: 'createdAt',
  order: 'desc'
};

// ==================== COLUMN VISIBILITY ====================

/**
 * Column visibility configuration
 * Controls which columns are displayed in the profile table
 */
export interface ColumnVisibility {
  categories: boolean;      // Categories pills
  lists: boolean;          // Lists pills
  score: boolean;          // Score badge (inline editable)
  gender: boolean;         // Gender quick-edit
  industry: boolean;       // Industry quick-edit
  company: boolean;        // Company text
  location: boolean;       // Location text
  email: boolean;          // Email text
  created: boolean;        // Created date
  exported: boolean;       // Exported date
  linkedinId: boolean;     // LinkedIn ID
  status: boolean;         // Status badges (active/blocked/registered)
}

/**
 * Default column visibility
 */
export const DEFAULT_COLUMN_VISIBILITY: ColumnVisibility = {
  categories: true,
  lists: true,
  score: true,
  gender: false,
  industry: false,
  company: true,
  location: true,
  email: false,
  created: false,
  exported: false,
  linkedinId: false,
  status: false,
};

// ==================== API RESPONSES ====================

/**
 * Profile search response from backend
 */
export interface ProfileSearchResponse {
  profiles: ProfileWithRelations[];
  pagination: PaginationMetadata;
  aggregations?: ProfileAggregations;
}

/**
 * Optional aggregations for analytics
 */
export interface ProfileAggregations {
  scoreRanges: Array<{ range: string; count: number }>;
  companies: Array<{ company: string; count: number }>;
  categories: Array<{ category: string; count: number }>;
}

// ==================== BULK OPERATIONS ====================

/**
 * Bulk operation request
 */
export interface BulkOperationRequest {
  mode: 'selected' | 'filtered';
  profileIds?: number[];              // Required if mode = 'selected'
  filters?: ProfileFilters;           // Required if mode = 'filtered'
  pagination?: PaginationState;       // Required if mode = 'filtered'
  sorting?: SortingState;             // Required if mode = 'filtered'
  updates?: Record<string, any>;      // For bulk updates
  format?: 'csv' | 'excel';           // For exports
  includeRelations?: boolean;
  action?: string; 
}

/**
 * Bulk operation result
 */
export interface BulkOperationResult {
  success: boolean;
  affectedCount: number;
  errors?: Array<{ 
    profileId: number; 
    error: string 
  }>;
  downloadUrl?: string;               // For export operations
  filename?: string;                  // For export operations
}

// ==================== EDIT DATA ====================

/**
 * Edit data for single or bulk profile updates
 * All fields optional - only changed fields are submitted
 */
export interface ProfileEditData {
  name?: string;
  title?: string;
  company?: string;
  location?: string;
  email?: string;
  gender?: string;
  industry?: string;
  score?: number;
}

// ==================== DATA VALUES ====================

/**
 * Data value option (for gender, industry dropdowns)
 */
export interface DataValueOption {
  id: string;
  label: string;
  order: number;
  isActive: boolean;
  description?: string;
}

// ==================== BULK SCOPE ====================

/**
 * Bulk operation scope information
 */
export interface BulkScope {
  type: 'selected' | 'filtered';
  count: number;
  label: string;
}

/**
 * Helper to determine bulk scope from selection and total
 */
export function getBulkScope(
  selectedCount: number,
  totalCount: number
): BulkScope {
  if (selectedCount > 0) {
    return {
      type: 'selected',
      count: selectedCount,
      label: `Selected (${selectedCount})`
    };
  }
  
  return {
    type: 'filtered',
    count: totalCount,
    label: `All Filtered (${totalCount})`
  };
}

// ==================== VALIDATION ====================

/**
 * Validation error for profile edit
 */
export interface ProfileEditValidationError {
  field: keyof ProfileEditData;
  message: string;
}

/**
 * Validate profile edit data
 */
export function validateProfileEdit(
  data: ProfileEditData,
  mode: 'single' | 'bulk'
): ProfileEditValidationError[] {
  const errors: ProfileEditValidationError[] = [];
  
  // Name validation (required in single mode)
  if (mode === 'single' && data.name !== undefined) {
    if (!data.name || data.name.trim().length === 0) {
      errors.push({ field: 'name', message: 'Name is required' });
    }
  }
  
  // Name length
  if (data.name && data.name.length > 255) {
    errors.push({ field: 'name', message: 'Name must be less than 255 characters' });
  }
  
  // Score validation
  if (data.score !== undefined) {
    if (isNaN(data.score) || data.score < 0 || data.score > 100) {
      errors.push({ field: 'score', message: 'Score must be between 0 and 100' });
    }
  }
  
  // Email validation
  if (data.email && data.email.trim() !== '') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      errors.push({ field: 'email', message: 'Please enter a valid email address' });
    }
  }
  
  // Field length validations
  if (data.title && data.title.length > 255) {
    errors.push({ field: 'title', message: 'Title must be less than 255 characters' });
  }
  
  if (data.company && data.company.length > 255) {
    errors.push({ field: 'company', message: 'Company must be less than 255 characters' });
  }
  
  if (data.location && data.location.length > 255) {
    errors.push({ field: 'location', message: 'Location must be less than 255 characters' });
  }
  
  if (data.email && data.email.length > 255) {
    errors.push({ field: 'email', message: 'Email must be less than 255 characters' });
  }
  
  return errors;
}

// ==================== DATE VALIDATION ====================

/**
 * Validate date range filter
 */
export interface DateRangeValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate date range (after date must be before before date)
 */
export function validateDateRange(
  afterDate?: string,
  beforeDate?: string
): DateRangeValidationResult {
  if (!afterDate || !beforeDate) {
    return { isValid: true };
  }
  
  const after = new Date(afterDate);
  const before = new Date(beforeDate);
  
  if (after > before) {
    return {
      isValid: false,
      error: '"After" date cannot be later than "Before" date'
    };
  }
  
  return { isValid: true };
}

// ==================== PERSISTENCE ====================

/**
 * Session storage keys for profile manager
 */
export const STORAGE_KEYS = {
  COLUMN_VISIBILITY: 'profileColumnVisibility',
  PAGE_LIMIT: 'profilePageLimit',
} as const;

/**
 * Load column visibility from session storage
 */
export function loadColumnVisibility(): ColumnVisibility {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEYS.COLUMN_VISIBILITY);
    if (saved) {
      return { ...DEFAULT_COLUMN_VISIBILITY, ...JSON.parse(saved) };
    }
  } catch (error) {
    console.error('[ProfileManager] Failed to load column visibility:', error);
  }
  return DEFAULT_COLUMN_VISIBILITY;
}

/**
 * Save column visibility to session storage
 */
export function saveColumnVisibility(visibility: ColumnVisibility): void {
  try {
    sessionStorage.setItem(
      STORAGE_KEYS.COLUMN_VISIBILITY,
      JSON.stringify(visibility)
    );
  } catch (error) {
    console.error('[ProfileManager] Failed to save column visibility:', error);
  }
}

/**
 * Load page limit from session storage
 */
export function loadPageLimit(): number {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEYS.PAGE_LIMIT);
    if (saved) {
      const limit = parseInt(saved);
      if (!isNaN(limit) && PAGE_SIZE_OPTIONS.includes(limit as any)) {
        return limit;
      }
    }
  } catch (error) {
    console.error('[ProfileManager] Failed to load page limit:', error);
  }
  return DEFAULT_PAGINATION.limit;
}

/**
 * Save page limit to session storage (only if <= 250)
 */
export function savePageLimit(limit: number): void {
  if (limit <= 250) {
    try {
      sessionStorage.setItem(STORAGE_KEYS.PAGE_LIMIT, limit.toString());
    } catch (error) {
      console.error('[ProfileManager] Failed to save page limit:', error);
    }
  }
}

// ==================== RE-EXPORTS FROM CORE ====================
// All core types available via @shared/types barrel export
// Listed here for documentation purposes only

// Core entity types (from @shared/types):
// - Profile, ProfileWithRelations (from core/profile.types.ts)
// - Category (from core/category.types.ts)
// - List (from core/list.types.ts)
// - User (from core/user.types.ts)
// - ScoreRange, GenderValue, IndustryValue (from core/setting.types.ts)
// - ProfileCategoryInfo, ProfileListInfo (from relations/relation-info.types.ts)